import type { CSSProperties } from 'react';

import { cn } from '../../../lib/utils';

export interface CountBadgeProps {
  /** `0` (or less) renders nothing — a badge is a presence cue, not a zero display. */
  count: number;
  /**
   * Accessible name. Optional, and usually omitted: where the badge sits inside a
   * control whose own `aria-label` already states the count ("Remind me — 2 reminders
   * set"), announcing it again reads the number twice.
   */
  ariaLabel?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * CountBadge — the standard "N of something needs your attention" corner badge.
 *
 * Positioned by the CALLER, because where it sits depends on the glyph it overlays:
 * pass `absolute -top-0.5 -end-0.5` (and give the trigger `relative`) for the app-bar
 * pattern, or nothing at all to render it inline beside a label.
 *
 * Two things it owns that a hand-written `<span>` keeps getting wrong:
 *
 *  - **the `99+` clamp**, without which a noisy record renders a four-digit pill that
 *    stretches its own trigger; and
 *  - **`tabular-nums`**, so a badge that ticks 9 → 10 → 11 does not jitter its width
 *    by a fraction of a character.
 *
 * Use the DESTRUCTIVE tone this renders for a count the user is expected to act on.
 * A read/unread thread indicator is a different contract — three states, accent tone —
 * and belongs to `ThreadUnreadBadge`.
 *
 * **Position it LOGICALLY.** A positioned caller passes `-end-*`, never the physical
 * `-right-*`: Tailwind compiles `-end-0.5` to `inset-inline-end`, which follows the
 * document direction, while `-right-0.5` puts the badge on the LEADING corner of its
 * glyph in every Hebrew and Arabic tenant. Nothing in the pipeline can see that — a
 * className is a plain string to `tsc`, ESLint sees ordinary JSX, and jsdom has no
 * layout engine, so a rendered test asserting the badge is present passes, because it
 * IS present, in the margin. The whole app bar carried the physical spelling until
 * WI cba1dbfc migrated it here.
 *
 * The negative key is what makes that safe. `tailwindcss-rtl` DOES emit
 * `[dir="rtl"] .end-3` / `[dir="ltr"] .end-3` descendant rules for POSITIVE inset
 * keys — that is the mechanism behind the trap that keeps a `NumericFieldBox` affix
 * inset spelled physically — but its generator walks `theme('inset')`'s raw keys and
 * never sees a negative one, so `-end-0.5` resolves through Tailwind core alone and
 * has no competing `[dir]`-scoped rule to lose to inside a nested `dir`.
 */
export function CountBadge({ count, ariaLabel, className, style }: CountBadgeProps) {
  if (count <= 0) return null;

  return (
    <span
      role="status"
      aria-label={ariaLabel}
      // Nothing to announce when the caller's own control already names the count —
      // and an unlabelled `role="status"` would otherwise read out the bare digits.
      aria-hidden={ariaLabel ? undefined : true}
      style={style}
      className={cn(
        'flex h-4 min-w-4 items-center justify-center rounded-full px-1',
        'text-[10px] font-bold leading-none tabular-nums',
        'bg-destructive text-destructive-foreground',
        className,
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}
